import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router";
import PrivateRoute from "./helpers/PrivateRoute";
import Header from "./components/Header";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import SignedupPage from "./components/SignedupPage";
import ProjectListPage from "./components/ProjectListPage";
import ProjectShowPage from "./components/ProjectShowPage";
import ProjectCreatePage from "./components/ProjectCreatePage";
import ProjectEditPage from "./components/ProjectEditPage";
import ProjectDeletePage from "./components/ProjectDeletePage";
import MaterialAddPage from "./components/MaterialAddPage";
import MaterialEditPage from "./components/MaterialEditPage";
import MaterialDeletePage from "./components/MaterialDeletePage";
import FiringShowPage from "./components/FiringShowPage";
import FiringEditPage from "./components/FiringEditPage";
import FiringDeletePage from "./components/FiringDeletePage";
import FiringCreatePage from "./components/FiringCreatePage";
import FiringFavouriteCreatePage from "./components/FiringFavouriteCreatePage";
import FiringFavouriteCopyConfirmPage from "./components/FiringFavouriteCopyConfirmPage";
import FiringTemplateCopyConfirmPage from "./components/FiringTemplateCopyConfirmPage";
import FiringExportPage from "./components/FiringExportPage";
import KilnsListPage from "./components/KilnsListPage";
import KilnCreatePage from "./components/KilnCreatePage";
import KilnShowPage from "./components/KilnShowPage";
import KilnEditPage from "./components/KilnEditPage";
import KilnDeletePage from "./components/KilnDeletePage";
import SegmentCreatePage from "./components/SegmentCreatePage";
import SegmentEditPage from "./components/SegmentEditPage";
import PrivacyPage from "./components/PrivacyPage";
import RockChoirPrivacyPage from "./components/RockChoirPrivacyPage";
import SupportPage from "./components/SupportPage";
import SegmentDeletePage from "./components/SegmentDeletePage";
import PreferencePage from "./components/PreferencePage";
import PasswordResetPage from "./components/PasswordResetPage";
import PasswordChangePage from "./components/PasswordChangePage";
import SuperUserPage from "./components/SuperUserPage";
import FavouriteFiringsListPage from "./components/FavouriteFiringsListPage";
import PhotoDeletePage from "./components/PhotoDeletePage";
import AboutPage from "./components/AboutPage";
import InventoryPage from "./components/InventoryPage";
import InventoryPrintPage from "./components/InventoryPrintPage";
import ProjectPrintPage from "./components/ProjectPrintPage";
import UserDeletePage from "./components/UserDeletePage";
import { useAlert } from "./contexts/AlertContext";
import { cn } from "./lib/utils";
import ToolsPage from "./components/ToolsPage";
import ColourWheelPage from "./components/ColourWheelPage";
import GlassWeightCalculatorPage from "./components/GlassWeightCalculatorPage";
import PremiumRoute from "./helpers/PremiumRoute";
import { AuthProvider } from "./helpers/Auth";
import { PremiumProvider } from "./contexts/PremiumContext";

const AlertClearer = () => {
  const location = useLocation();
  const { clearAlert } = useAlert();
  useEffect(() => {
    clearAlert();
  }, [location, clearAlert]);
  return null;
};

const App = () => {
  const { alert } = useAlert();

  return (
    <div className="max-w-5xl mx-auto px-4">
      <AuthProvider>
        <PremiumProvider>
        <BrowserRouter>
          <AlertClearer />
          <Header />
          {alert && alert.message && (
            <div className={cn(
              "rounded-md p-4 mb-4 border",
              (alert.type as string) === "positive" || alert.type === "success"
                ? "bg-success-bg text-success-foreground border-success/30"
                : (alert.type as string) === "negative" || alert.type === "error"
                ? "bg-destructive/10 text-destructive border-destructive/30"
                : alert.type === "warning"
                ? "bg-warning-bg text-warning-foreground border-warning/30"
                : "bg-info-bg text-info-foreground border-info/30"
            )}>{alert.message}</div>
          )}
          <div>
              <Routes>
                <Route path="/" element={<AboutPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/signedup" element={<SignedupPage />} />
                <Route path="/reset-password" element={<PasswordResetPage />} />
                <Route path="/change-password" element={<PrivateRoute><PasswordChangePage /></PrivateRoute>} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/rock_choir_privacy" element={<RockChoirPrivacyPage />} />
                <Route path="/support" element={<SupportPage />} />

                <Route path="/projects" element={<PrivateRoute><ProjectListPage /></PrivateRoute>} />
                <Route path="/projects/:id" element={<PrivateRoute><ProjectShowPage /></PrivateRoute>} />
                <Route
                  path="/shared_project/:sharing_user_id/:shared_project_id"
                  element={<ProjectShowPage />}
                />
                <Route path="/new_project" element={<PrivateRoute><ProjectCreatePage /></PrivateRoute>} />
                <Route path="/projects/edit/:id" element={<PrivateRoute><ProjectEditPage /></PrivateRoute>} />
                <Route path="/projects/print/:id" element={<PrivateRoute><ProjectPrintPage /></PrivateRoute>} />
                <Route path="/projects/delete/:id" element={<PrivateRoute><ProjectDeletePage /></PrivateRoute>} />
                <Route
                  path="/projects/delete_photo/:id/:index"
                  element={<PrivateRoute><PhotoDeletePage /></PrivateRoute>}
                />
                <Route path="/firings/:id" element={<PrivateRoute><FiringShowPage /></PrivateRoute>} />
                <Route
                  path="/shared_firing/:sharing_user_id/:shared_firing_id"
                  element={<FiringShowPage />}
                />
                <Route path="/new_firing/:id/:order" element={<PrivateRoute><FiringCreatePage /></PrivateRoute>} />
                <Route
                  path="/new_favourite_firing/:id"
                  element={<PrivateRoute><FiringFavouriteCreatePage /></PrivateRoute>}
                />
                <Route
                  path="/copy_favourite_firing_confirm/:project_id/:firing_id"
                  element={<PrivateRoute><FiringFavouriteCopyConfirmPage /></PrivateRoute>}
                />
                <Route
                  path="/copy_template_firing_confirm/:project_id/:firing_id/:glass_type"
                  element={<PrivateRoute><FiringTemplateCopyConfirmPage /></PrivateRoute>}
                />
                <Route path="/firing/delete/:id" element={<PrivateRoute><FiringDeletePage /></PrivateRoute>} />
                <Route
                  path="/firing/export/:kiln/:id"
                  element={<PrivateRoute><FiringExportPage /></PrivateRoute>}
                />
                <Route path="/firings/edit/:id" element={<PrivateRoute><FiringEditPage /></PrivateRoute>} />
                <Route path="/new_material/:id/:order" element={<PrivateRoute><MaterialAddPage /></PrivateRoute>} />
                <Route
                  path="/edit_material/:project_id/:material_id"
                  element={<PrivateRoute><MaterialEditPage /></PrivateRoute>}
                />
                <Route
                  path="/delete_material/:project_id/:material_id"
                  element={<PrivateRoute><MaterialDeletePage /></PrivateRoute>}
                />
                <Route path="/kilns" element={<PrivateRoute><KilnsListPage /></PrivateRoute>} />
                <Route path="/new_kiln" element={<PrivateRoute><KilnCreatePage /></PrivateRoute>} />
                <Route path="/kilns/:id" element={<PrivateRoute><KilnShowPage /></PrivateRoute>} />
                <Route path="/kilns/edit/:id" element={<PrivateRoute><KilnEditPage /></PrivateRoute>} />
                <Route path="/kilns/delete/:id" element={<PrivateRoute><KilnDeletePage /></PrivateRoute>} />
                <Route path="/inventory" element={<PrivateRoute><PremiumRoute><InventoryPage /></PremiumRoute></PrivateRoute>} />
                <Route path="/inventory/print" element={<PrivateRoute><PremiumRoute><InventoryPrintPage /></PremiumRoute></PrivateRoute>} />
                <Route
                  path="/new_segment/:id/:order"
                  element={<PrivateRoute><SegmentCreatePage /></PrivateRoute>}
                />
                <Route path="/segments/edit/:id" element={<PrivateRoute><SegmentEditPage /></PrivateRoute>} />
                <Route path="/segments/delete/:id" element={<PrivateRoute><SegmentDeletePage /></PrivateRoute>} />
                <Route path="/tools" element={<PrivateRoute><ToolsPage /></PrivateRoute>} />
                <Route path="/tools/colour-wheel" element={<PrivateRoute><PremiumRoute><ColourWheelPage /></PremiumRoute></PrivateRoute>} />
                <Route path="/tools/weight-calculator" element={<PrivateRoute><GlassWeightCalculatorPage /></PrivateRoute>} />
                <Route path="/preferences" element={<PrivateRoute><PreferencePage /></PrivateRoute>} />
                <Route path="/user_delete" element={<PrivateRoute><UserDeletePage /></PrivateRoute>} />
                <Route path="/superuser" element={<PrivateRoute><SuperUserPage /></PrivateRoute>} />
                <Route
                  path="/favourite_firings"
                  element={<PrivateRoute><FavouriteFiringsListPage /></PrivateRoute>}
                />
              </Routes>
          </div>
        </BrowserRouter>
        </PremiumProvider>
      </AuthProvider>
    </div>
  );
};

export default App;
